// components/CreditConfigManager.js
import React, { useState, useEffect } from "react";
import {
  Plus,
  Edit,
  Trash2,
  Settings,
  Gift,
  AlertCircle,
  X,
} from "lucide-react";
import api from "./api";
import "./CreditConfigManager.css";

const CreditConfigManager = () => {
  const [configs, setConfigs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [formData, setFormData] = useState({
    configType: "default",
    name: "",
    code: "",
    creditsOnPaymentMethod: 0,
    creditsOnRegistration: 0,
    maxUsage: "",
    expiresAt: "",
  });

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      const response = await api.get("/admin/credit-configs");
      setConfigs(response.data);
      setLoading(false);
    } catch (err) {
      setError("Failed to fetch credit configurations");
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    try {
      const payload = {
        ...formData,
        maxUsage: formData.maxUsage ? parseInt(formData.maxUsage) : undefined,
        expiresAt: formData.expiresAt
          ? new Date(formData.expiresAt)
          : undefined,
      };

      if (editingConfig) {
        await api.patch(`/admin/credit-configs/${editingConfig._id}`, payload);
      } else {
        await api.post("/admin/credit-configs", payload);
      }

      await fetchConfigs();
      resetForm();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save configuration");
    }
  };

  const handleEdit = (config) => {
    setEditingConfig(config);
    setFormData({
      configType: config.configType,
      name: config.name,
      code: config.code || "",
      creditsOnPaymentMethod: config.creditsOnPaymentMethod,
      creditsOnRegistration: config.creditsOnRegistration,
      maxUsage: config.maxUsage || "",
      expiresAt: config.expiresAt
        ? new Date(config.expiresAt).toISOString().split("T")[0]
        : "",
    });
    setShowForm(true);
  };

  const handleDelete = async (configId) => {
    if (
      !window.confirm("Are you sure you want to delete this configuration?")
    ) {
      return;
    }

    try {
      await api.delete(`/admin/credit-configs/${configId}`);
      await fetchConfigs();
    } catch (err) {
      setError("Failed to delete configuration");
    }
  };

  const toggleActive = async (config) => {
    try {
      await api.patch(`/admin/credit-configs/${config._id}`, {
        isActive: !config.isActive,
      });
      await fetchConfigs();
    } catch (err) {
      setError("Failed to update configuration status");
    }
  };

  const resetForm = () => {
    setFormData({
      configType: "default",
      name: "",
      code: "",
      creditsOnPaymentMethod: 0,
      creditsOnRegistration: 0,
      maxUsage: "",
      expiresAt: "",
    });
    setEditingConfig(null);
    setShowForm(false);
  };

  const generateDealLink = (code) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/signup?deal=${code}`;
  };

  if (loading) return <div className="loading">Loading configurations...</div>;

  return (
    <div className="credit-config-container">
      <div className="config-header">
        <h2>Credit Configuration</h2>
        <button className="btn-primary" onClick={() => setShowForm(true)}>
          <Plus size={18} />
          New Configuration
        </button>
      </div>

      {error && (
        <div className="error-alert">
          <AlertCircle size={20} />
          {error}
        </div>
      )}

      <div className="configs-grid">
        {configs.map((config) => (
          <div
            key={config._id}
            className={`config-card ${config.isActive ? "active" : "inactive"}`}
          >
            <div className="config-info">
              <div className="config-header-row">
                <h3>
                  {config.configType === "default" ? (
                    <Settings size={20} />
                  ) : (
                    <Gift size={20} />
                  )}
                  {config.name}
                </h3>
                <span
                  className={`status-badge ${
                    config.isActive ? "active" : "inactive"
                  }`}
                >
                  {config.isActive ? "Active" : "Inactive"}
                </span>
              </div>

              <div className="config-details">
                {config.code && (
                  <div className="detail-row">
                    <strong>Code:</strong> {config.code}
                  </div>
                )}
                <div className="detail-row">
                  <strong>Registration Credits:</strong>{" "}
                  {config.creditsOnRegistration}
                </div>
                <div className="detail-row">
                  <strong>Payment Method Credits:</strong>{" "}
                  {config.creditsOnPaymentMethod}
                </div>
                {config.usageCount > 0 && (
                  <div className="detail-row">
                    <strong>Used:</strong> {config.usageCount}
                    {config.maxUsage && ` / ${config.maxUsage}`}
                  </div>
                )}
                {config.expiresAt && (
                  <div className="detail-row">
                    <strong>Expires:</strong>{" "}
                    {new Date(config.expiresAt).toLocaleDateString()}
                  </div>
                )}
              </div>

              {config.code && (
                <div className="deal-link">
                  <strong>Deal Link:</strong>
                  <input
                    type="text"
                    value={generateDealLink(config.code)}
                    readOnly
                    className="deal-link-input"
                    onClick={(e) => e.target.select()}
                  />
                </div>
              )}
            </div>

            <div className="config-actions">
              <button
                onClick={() => toggleActive(config)}
                className={`btn-toggle ${
                  config.isActive ? "active" : "inactive"
                }`}
              >
                {config.isActive ? "Deactivate" : "Activate"}
              </button>
              <button onClick={() => handleEdit(config)} className="btn-edit">
                <Edit size={16} />
              </button>
              <button
                onClick={() => handleDelete(config._id)}
                className="btn-delete"
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="modal">
          <div className="modal-content large">
            <div className="modal-header">
              <h3>{editingConfig ? "Edit" : "Create"} Credit Configuration</h3>
              <button className="modal-close" onClick={resetForm}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="config-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Type</label>
                  <select
                    value={formData.configType}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        configType: e.target.value,
                      }))
                    }
                    required
                  >
                    <option value="default">Default</option>
                    <option value="deal">Deal Code</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, name: e.target.value }))
                    }
                    required
                    placeholder="e.g., 'Default Setup' or 'Black Friday Deal'"
                  />
                </div>
              </div>

              {formData.configType === "deal" && (
                <div className="form-group">
                  <label>Deal Code</label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        code: e.target.value.toUpperCase(),
                      }))
                    }
                    required
                    placeholder="e.g., 'WELCOME50' or 'BLACKFRIDAY'"
                  />
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>Credits on Registration</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.creditsOnRegistration}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        creditsOnRegistration: parseInt(e.target.value) || 0,
                      }))
                    }
                  />
                  <small>Credits given immediately upon signup</small>
                </div>

                <div className="form-group">
                  <label>Credits on Payment Method</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.creditsOnPaymentMethod}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        creditsOnPaymentMethod: parseInt(e.target.value) || 0,
                      }))
                    }
                  />
                  <small>Credits given when user adds payment method</small>
                </div>
              </div>

              {formData.configType === "deal" && (
                <div className="form-row">
                  <div className="form-group">
                    <label>Max Usage (optional)</label>
                    <input
                      type="number"
                      min="1"
                      value={formData.maxUsage}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          maxUsage: e.target.value,
                        }))
                      }
                      placeholder="Leave empty for unlimited"
                    />
                  </div>

                  <div className="form-group">
                    <label>Expires At (optional)</label>
                    <input
                      type="date"
                      value={formData.expiresAt}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          expiresAt: e.target.value,
                        }))
                      }
                    />
                  </div>
                </div>
              )}

              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  {editingConfig ? "Update" : "Create"} Configuration
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreditConfigManager;
